// src/components/employee/EmployeeTaskManagement.jsx

import React, { useState, useEffect } from 'react';
import {
  Table,
  Button,
  Modal,
  Form,
  Select,
  DatePicker,
  Input,
  Tag,
  Space,
  Card,
  Row,
  Col,
  Typography,
  Popconfirm,
  message,
  Badge,
  Tooltip
} from 'antd';
import {
  Plus,
  Edit2,
  Trash2,
  Eye,
  Calendar,
  User,
  Flag,
  CheckCircle,
  Clock,
  AlertCircle
} from 'lucide-react';
import moment from 'moment';
import EmployeeTaskService from '../../../services/EmployeeTaskService';
import EmployeeService from '../../../services/EmployeeService';

const { Title } = Typography;
const { TextArea } = Input;
const { Option } = Select;

const EmployeeTaskManagement = () => {
  const [tasks, setTasks] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [loading, setLoading] = useState(false);
  const [modalVisible, setModalVisible] = useState(false);
  const [detailModalVisible, setDetailModalVisible] = useState(false);
  const [editingTask, setEditingTask] = useState(null);
  const [selectedTask, setSelectedTask] = useState(null);
  const [form] = Form.useForm();
  const [commentForm] = Form.useForm();
  const [filterStatus, setFilterStatus] = useState('all');
  const [filterPriority, setFilterPriority] = useState('all');

  useEffect(() => {
    fetchTasks();
    fetchEmployees();
  }, []);

  const fetchTasks = async () => {
    setLoading(true);
    try {
      const data = await EmployeeTaskService.getAllTasks();
      setTasks(data);
    } catch (error) {
      message.error('Failed to fetch tasks');
    } finally {
      setLoading(false);
    }
  };

  const fetchEmployees = async () => {
    try {
      const data = await EmployeeService.getAllEmployees();
      setEmployees(data);
    } catch (error) {
      console.error('Failed to fetch employees');
    }
  };

  const handleCreate = () => {
    setEditingTask(null);
    form.resetFields();
    setModalVisible(true);
  };

  const handleEdit = (record) => {
    setEditingTask(record);
    form.setFieldsValue({
      ...record,
      dueDate: record.dueDate ? moment(record.dueDate) : null
    });
    setModalVisible(true);
  };

  const handleDelete = async (id) => {
    try {
      await EmployeeTaskService.deleteTask(id);
      message.success('Task deleted successfully');
      fetchTasks();
    } catch (error) {
      message.error('Failed to delete task');
    }
  };

  const handleSubmit = async (values) => {
    try {
      const taskData = {
        ...values,
        dueDate: values.dueDate ? values.dueDate.toISOString() : null
      };

      if (editingTask) {
        await EmployeeTaskService.updateTask(editingTask._id, taskData);
        message.success('Task updated successfully');
      } else {
        await EmployeeTaskService.createTask(taskData);
        message.success('Task created successfully');
      }

      setModalVisible(false);
      form.resetFields();
      fetchTasks();
    } catch (error) {
      message.error('Failed to save task');
    }
  };

  const handleStatusChange = async (taskId, newStatus) => {
    try {
      const completedAt = newStatus === 'completed' ? new Date().toISOString() : null;
      await EmployeeTaskService.updateTaskStatus(taskId, newStatus, completedAt);
      message.success('Task status updated');
      fetchTasks();
    } catch (error) {
      message.error('Failed to update task status');
    }
  };

  const handleViewDetails = (record) => {
    setSelectedTask(record);
    setDetailModalVisible(true);
  };

  const getStatusColor = (status) => {
    const colors = {
      pending: 'orange',
      'in-progress': 'blue',
      completed: 'green',
      cancelled: 'red'
    };
    return colors[status] || 'default';
  };

  const getPriorityColor = (priority) => {
    const colors = {
      low: 'green',
      medium: 'orange',
      high: 'red'
    };
    return colors[priority] || 'default';
  };

  const getStatusIcon = (status) => {
    const icons = {
      pending: <Clock className="w-4 h-4" />,
      'in-progress': <AlertCircle className="w-4 h-4" />,
      completed: <CheckCircle className="w-4 h-4" />,
      cancelled: <AlertCircle className="w-4 h-4" />
    };
    return icons[status] || <Clock className="w-4 h-4" />;
  };

  const isOverdue = (dueDate, status) => {
    if (!dueDate || status === 'completed') return false;
    return moment(dueDate).isBefore(moment(), 'day');
  };

  const filteredTasks = tasks.filter(task => {
    const statusMatch = filterStatus === 'all' || task.status === filterStatus;
    const priorityMatch = filterPriority === 'all' || task.priority === filterPriority;
    return statusMatch && priorityMatch;
  });

  const getEmployeeName = (employeeId) => {
    const employee = employees.find(emp => emp._id === employeeId);
    return employee ? `${employee.personalInfo.firstName} ${employee.personalInfo.lastName}` : 'Unknown Employee';
  };

  const columns = [
    {
      title: 'Title',
      dataIndex: 'title',
      key: 'title',
      render: (text, record) => (
        <div className="flex flex-col">
          <span className="font-semibold text-gray-900">{text}</span>
          {isOverdue(record.dueDate, record.status) && (
            <Badge count="Overdue" className="bg-red-500 text-white text-xs px-2 py-1 rounded mt-1" />
          )}
        </div>
      )
    },
    {
      title: 'Assigned To',
      dataIndex: 'assignedTo',
      key: 'assignedTo',
      render: (employeeId) => (
        <div className="flex items-center space-x-2">
          <User className="w-4 h-4 text-gray-500" />
          <span className="text-gray-700">{getEmployeeName(employeeId)}</span>
        </div>
      )
    },
    {
      title: 'Status',
      dataIndex: 'status',
      key: 'status',
      render: (status) => (
        <Tag color={getStatusColor(status)} className="flex items-center space-x-1">
          {getStatusIcon(status)}
          <span>{status.toUpperCase()}</span>
        </Tag>
      )
    },
    {
      title: 'Priority',
      dataIndex: 'priority',
      key: 'priority',
      render: (priority) => (
        <Tag color={getPriorityColor(priority)} className="flex items-center space-x-1">
          <Flag className="w-4 h-4" />
          <span>{priority.toUpperCase()}</span>
        </Tag>
      )
    },
    {
      title: 'Due Date',
      dataIndex: 'dueDate',
      key: 'dueDate',
      render: (date) => date ? (
        <div className="flex items-center space-x-2">
          <Calendar className="w-4 h-4 text-gray-500" />
          <span className="text-gray-700">{moment(date).format('MMM DD, YYYY')}</span>
        </div>
      ) : (
        <span className="text-gray-400">-</span>
      )
    },
    {
      title: 'Created',
      dataIndex: 'createdAt',
      key: 'createdAt',
      render: (date) => (
        <span className="text-gray-600">{moment(date).format('MMM DD, YYYY')}</span>
      )
    },
    {
      title: 'Actions',
      key: 'actions',
      render: (_, record) => (
        <Space size="small" className="flex items-center">
          <Tooltip title="View Details">
            <Button
              type="text"
              className="p-1 hover:bg-gray-100 rounded-md"
              onClick={() => handleViewDetails(record)}
            >
              <Eye className="w-4 h-4 text-blue-600" />
            </Button>
          </Tooltip>
          <Tooltip title="Edit">
            <Button
              type="text"
              className="p-1 hover:bg-gray-100 rounded-md"
              onClick={() => handleEdit(record)}
            >
              <Edit2 className="w-4 h-4 text-green-600" />
            </Button>
          </Tooltip>
          <Tooltip title="Change Status">
            <Select
              value={record.status}
              className="w-28"
              onChange={(value) => handleStatusChange(record._id, value)}
              size="small"
            >
              <Option value="pending">Pending</Option>
              <Option value="in-progress">In Progress</Option>
              <Option value="completed">Completed</Option>
              <Option value="cancelled">Cancelled</Option>
            </Select>
          </Tooltip>
          <Popconfirm
            title="Are you sure you want to delete this task?"
            onConfirm={() => handleDelete(record._id)}
            okText="Yes"
            cancelText="No"
          >
            <Tooltip title="Delete">
              <Button
                type="text"
                danger
                className="p-1 hover:bg-red-50 rounded-md"
              >
                <Trash2 className="w-4 h-4 text-red-600" />
              </Button>
            </Tooltip>
          </Popconfirm>
        </Space>
      )
    }
  ];

  const taskStats = {
    total: tasks.length,
    pending: tasks.filter(t => t.status === 'pending').length,
    inProgress: tasks.filter(t => t.status === 'in-progress').length,
    completed: tasks.filter(t => t.status === 'completed').length,
    overdue: tasks.filter(t => isOverdue(t.dueDate, t.status)).length
  };

  return (
    <div className="p-6 bg-gray-50 min-h-screen">
      {/* Header */}
      <div className="mb-6">
        <div className="flex justify-between items-center">
          <Title level={2} className="text-gray-800 mb-0">Employee Task Management</Title>
          <Button
            type="primary"
            onClick={handleCreate}
            className="flex items-center space-x-2 bg-blue-600 hover:bg-blue-700 border-blue-600"
          >
            <Plus className="w-4 h-4" />
            <span>Create Task</span>
          </Button>
        </div>
      </div>

      {/* Stats Cards */}
      <Row gutter={[16, 16]} className="mb-6">
        <Col xs={24} sm={12} md={6}>
          <Card className="text-center bg-white shadow-sm border-0 rounded-lg">
            <div className="p-4">
              <div className="text-3xl font-bold text-blue-600 mb-2">
                {taskStats.total}
              </div>
              <p className="text-gray-600 mb-0">Total Tasks</p>
            </div>
          </Card>
        </Col>
        <Col xs={24} sm={12} md={6}>
          <Card className="text-center bg-white shadow-sm border-0 rounded-lg">
            <div className="p-4">
              <div className="text-3xl font-bold text-orange-500 mb-2">
                {taskStats.pending}
              </div>
              <p className="text-gray-600 mb-0">Pending</p>
            </div>
          </Card>
        </Col>
        <Col xs={24} sm={12} md={6}>
          <Card className="text-center bg-white shadow-sm border-0 rounded-lg">
            <div className="p-4">
              <div className="text-3xl font-bold text-green-500 mb-2">
                {taskStats.completed}
              </div>
              <p className="text-gray-600 mb-0">Completed</p>
            </div>
          </Card>
        </Col>
        <Col xs={24} sm={12} md={6}>
          <Card className="text-center bg-white shadow-sm border-0 rounded-lg">
            <div className="p-4">
              <div className="text-3xl font-bold text-red-500 mb-2">
                {taskStats.overdue}
              </div>
              <p className="text-gray-600 mb-0">Overdue</p>
            </div>
          </Card>
        </Col>
      </Row>

      {/* Filters */}
      <Row gutter={[16, 16]} className="mb-6">
        <Col xs={24} sm={12} md={6}>
          <Select
            placeholder="Filter by Status"
            className="w-full"
            value={filterStatus}
            onChange={setFilterStatus}
          >
            <Option value="all">All Status</Option>
            <Option value="pending">Pending</Option>
            <Option value="in-progress">In Progress</Option>
            <Option value="completed">Completed</Option>
            <Option value="cancelled">Cancelled</Option>
          </Select>
        </Col>
        <Col xs={24} sm={12} md={6}>
          <Select
            placeholder="Filter by Priority"
            className="w-full"
            value={filterPriority}
            onChange={setFilterPriority}
          >
            <Option value="all">All Priority</Option>
            <Option value="low">Low</Option>
            <Option value="medium">Medium</Option>
            <Option value="high">High</Option>
          </Select>
        </Col>
      </Row>

      {/* Tasks Table */}
      <Card className="bg-white shadow-sm border-0 rounded-lg">
        <Table
          columns={columns}
          dataSource={filteredTasks}
          rowKey="_id"
          loading={loading}
          scroll={{ x: 'max-content' }}
          pagination={{
            pageSize: 10,
            showSizeChanger: true,
            showQuickJumper: true,
            showTotal: (total, range) => `${range[0]}-${range[1]} of ${total} tasks`,
            className: "px-4 py-2"
          }}
          className="rounded-lg overflow-hidden"
        />
      </Card>

      {/* Create/Edit Modal */}
      <Modal
        title={
          <div className="flex items-center space-x-2">
            {editingTask ? <Edit2 className="w-5 h-5" /> : <Plus className="w-5 h-5" />}
            <span>{editingTask ? 'Edit Task' : 'Create New Task'}</span>
          </div>
        }
        visible={modalVisible}
        onCancel={() => setModalVisible(false)}
        footer={null}
        width={600}
        className="rounded-lg"
      >
        <Form
          form={form}
          layout="vertical"
          onFinish={handleSubmit}
          className="mt-4"
        >
          <Form.Item
            name="title"
            label={<span className="text-gray-700 font-medium">Task Title</span>}
            rules={[{ required: true, message: 'Please enter task title' }]}
          >
            <Input 
              placeholder="Enter task title" 
              className="rounded-md border-gray-300 focus:border-blue-500 focus:ring-blue-500"
            />
          </Form.Item>

          <Form.Item
            name="description"
            label={<span className="text-gray-700 font-medium">Description</span>}
          >
            <TextArea
              rows={4}
              placeholder="Enter task description"
              className="rounded-md border-gray-300 focus:border-blue-500 focus:ring-blue-500"
            />
          </Form.Item>

          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                name="assignedTo"
                label={<span className="text-gray-700 font-medium">Assign To</span>}
                rules={[{ required: true, message: 'Please select an employee' }]}
              >
                <Select 
                  placeholder="Select employee"
                  className="w-full"
                >
                  {employees.map(employee => (
                    <Option key={employee._id} value={employee._id}>
                      <div className="flex items-center space-x-2">
                        <User className="w-4 h-4" />
                        <span>{employee.personalInfo.firstName} {employee.personalInfo.lastName}</span>
                      </div>
                    </Option>
                  ))}
                </Select>
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                name="priority"
                label={<span className="text-gray-700 font-medium">Priority</span>}
                initialValue="medium"
              >
                <Select className="w-full">
                  <Option value="low">
                    <div className="flex items-center space-x-2">
                      <Flag className="w-4 h-4 text-green-500" />
                      <span>Low</span>
                    </div>
                  </Option>
                  <Option value="medium">
                    <div className="flex items-center space-x-2">
                      <Flag className="w-4 h-4 text-orange-500" />
                      <span>Medium</span>
                    </div>
                  </Option>
                  <Option value="high">
                    <div className="flex items-center space-x-2">
                      <Flag className="w-4 h-4 text-red-500" />
                      <span>High</span>
                    </div>
                  </Option>
                </Select>
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                name="status"
                label={<span className="text-gray-700 font-medium">Status</span>}
                initialValue="pending"
              >
                <Select className="w-full">
                  <Option value="pending">Pending</Option>
                  <Option value="in-progress">In Progress</Option>
                  <Option value="completed">Completed</Option>
                  <Option value="cancelled">Cancelled</Option>
                </Select>
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                name="dueDate"
                label={<span className="text-gray-700 font-medium">Due Date</span>}
              >
                <DatePicker
                  className="w-full rounded-md border-gray-300 focus:border-blue-500 focus:ring-blue-500"
                  format="YYYY-MM-DD"
                />
              </Form.Item>
            </Col>
          </Row>

          <Form.Item className="mb-0 text-right">
            <Space>
              <Button 
                onClick={() => setModalVisible(false)}
                className="px-4 py-2 border border-gray-300 rounded-md hover:bg-gray-50"
              >
                Cancel
              </Button>
              <Button 
                type="primary" 
                htmlType="submit"
                className="px-4 py-2 bg-blue-600 hover:bg-blue-700 border-blue-600 rounded-md"
              >
                {editingTask ? 'Update Task' : 'Create Task'}
              </Button>
            </Space>
          </Form.Item>
        </Form>
      </Modal>

      {/* Task Detail Modal */}
      <Modal
        title={
          <div className="flex items-center space-x-2">
            <Eye className="w-5 h-5" />
            <span>Task Details</span>
          </div>
        }
        visible={detailModalVisible}
        onCancel={() => setDetailModalVisible(false)}
        footer={null}
        width={700}
        className="rounded-lg"
      >
        {selectedTask && (
          <div className="mt-4">
            <div className="mb-6">
              <Title level={4} className="text-gray-800 mb-2">{selectedTask.title}</Title>
              <p className="text-gray-600">{selectedTask.description}</p>
            </div>

            <div className="grid grid-cols-2 gap-4 mb-4">
              <div>
                <span className="font-semibold text-gray-700">Assigned To:</span>
                <div className="flex items-center space-x-2 mt-1">
                  <User className="w-4 h-4 text-gray-500" />
                  <span className="text-gray-700">{getEmployeeName(selectedTask.assignedTo)}</span>
                </div>
              </div>
              <div>
                <span className="font-semibold text-gray-700">Status:</span>
                <div className="mt-1">
                  <Tag color={getStatusColor(selectedTask.status)} className="flex items-center space-x-1 inline-flex">
                    {getStatusIcon(selectedTask.status)}
                    <span>{selectedTask.status.toUpperCase()}</span>
                  </Tag>
                </div>
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4 mb-4">
              <div>
                <span className="font-semibold text-gray-700">Priority:</span>
                <div className="mt-1">
                  <Tag color={getPriorityColor(selectedTask.priority)} className="flex items-center space-x-1 inline-flex">
                    <Flag className="w-4 h-4" />
                    <span>{selectedTask.priority.toUpperCase()}</span>
                  </Tag>
                </div>
              </div>
              <div>
                <span className="font-semibold text-gray-700">Due Date:</span>
                <div className="flex items-center space-x-2 mt-1">
                  <Calendar className="w-4 h-4 text-gray-500" />
                  <span className="text-gray-700">
                    {selectedTask.dueDate ? moment(selectedTask.dueDate).format('MMM DD, YYYY') : 'No due date'}
                  </span>
                </div>
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4 mb-4">
              <div>
                <span className="font-semibold text-gray-700">Created:</span>
                <div className="flex items-center space-x-2 mt-1">
                  <Calendar className="w-4 h-4 text-gray-500" />
                  <span className="text-gray-700">{moment(selectedTask.createdAt).format('MMM DD, YYYY')}</span>
                </div>
              </div>
              <div>
                <span className="font-semibold text-gray-700">Completed:</span>
                <div className="flex items-center space-x-2 mt-1">
                  {selectedTask.completedAt ? (
                    <>
                      <CheckCircle className="w-4 h-4 text-green-500" />
                      <span className="text-gray-700">{moment(selectedTask.completedAt).format('MMM DD, YYYY')}</span>
                    </>
                  ) : (
                    <span className="text-gray-400">Not completed</span>
                  )}
                </div>
              </div>
            </div>

            {selectedTask.comments && selectedTask.comments.length > 0 && (
              <div className="mt-6">
                <Title level={5} className="text-gray-800 mb-3">Comments:</Title>
                <div className="space-y-3">
                  {selectedTask.comments.map((comment, index) => (
                    <Card key={index} size="small" className="bg-gray-50 border-gray-200">
                      <p className="text-gray-700 mb-2">{comment.comment}</p>
                      <small className="text-gray-500">
                        {getEmployeeName(comment.user)} - {moment(comment.createdAt).format('MMM DD, YYYY HH:mm')}
                      </small>
                    </Card>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}
      </Modal>
    </div>
  );
};

export default EmployeeTaskManagement;